import React, { useState, useEffect, useRef } from 'react';

const TimeRangeSelector = ({
    labelFrom = 'From',
    labelTo = 'To',
    fromTime,
    toTime,
    setFromTime,
    setToTime,
    className = 'time-picker',
    disabled = false,
}) => {
    const [error, setError] = useState('');
    const [showFromPicker, setShowFromPicker] = useState(false);
    const [showToPicker, setShowToPicker] = useState(false);
    const [fromInputValue, setFromInputValue] = useState(fromTime || '');
    const [toInputValue, setToInputValue] = useState(toTime || '');
    const fromWrapperRef = useRef(null);
    const toWrapperRef = useRef(null);

    useEffect(() => {
        setFromInputValue(fromTime ?? '');
        setToInputValue(toTime ?? '');
    }, [fromTime, toTime]);


    // Close pickers when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (fromWrapperRef.current && !fromWrapperRef.current.contains(event.target)) {
                setShowFromPicker(false);
                validateAndSetFromTime(fromInputValue);
            }
            if (toWrapperRef.current && !toWrapperRef.current.contains(event.target)) {
                setShowToPicker(false);
                validateAndSetToTime(toInputValue);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [fromInputValue, toInputValue]);

    // Generate time options in 30-minute intervals
    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                options.push(timeValue);
            }
        }
        return options;
    };

    const timeOptions = generateTimeOptions();

    // Convert time string to minutes for comparison
    const timeToMinutes = (time) => {
        if (!time) return 0;
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    };

    // Validate time range
    const validateTimes = () => {
        if (!fromTime || !toTime) {
            setError('');
            return;
        }

        const fromMinutes = timeToMinutes(fromTime);
        const toMinutes = timeToMinutes(toTime);

        if (toMinutes <= fromMinutes) {
            setError(`${labelTo} må være senere enn ${labelFrom}`);
        } else {
            setError('');
        }
    };

    // Handle from time change with auto-adjustment
    const handleFromTimeChange = (newFromTime) => {
        setFromTime(newFromTime);
        setFromInputValue(newFromTime);
        setShowFromPicker(false);

        if (toTime && timeToMinutes(newFromTime) >= timeToMinutes(toTime)) {
            const newToMinutes = timeToMinutes(newFromTime) + 30;
            const hours = Math.floor(newToMinutes / 60) % 24;
            const minutes = newToMinutes % 60;
            const newToTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            setToTime(newToTime);
            setToInputValue(newToTime);
        }
    };

    // Handle to time change
    const handleToTimeChange = (newToTime) => {
        setToTime(newToTime);
        setToInputValue(newToTime);
        setShowToPicker(false);
    };

    // Calculate duration
    const getDuration = () => {
        if (!fromTime || !toTime || error) return '';

        const duration = timeToMinutes(toTime) - timeToMinutes(fromTime);
        const hours = Math.floor(duration / 60);
        const minutes = duration % 60;

        if (hours === 0 && minutes > 0) {
            // Under 1 time, bare minutter
            return `${minutes} minutter`;
        } else if (hours === 1 && minutes === 0) {
            // Eksakt 1 time
            return `1 time`;
        } else if (hours > 1 && minutes === 0) {
            // Flere timer, ingen minutter
            return `${hours} timer`;
        } else if (hours >= 1 && minutes > 0) {
            // Flere timer og minutter
            return `${hours} timer og ${minutes} minutter`;
        } else {
            // Hvis varigheten er null eller noe annet
            return '';
        }
    };

    // Format time input with masking
    const formatTimeInput = (value) => {
        // Remove all non-digit characters
        let digits = value.replace(/\D/g, '');

        // Add colon after first two digits
        if (digits.length > 2) {
            digits = `${digits.substring(0, 2)}:${digits.substring(2)}`;
        }

        // Limit to 5 characters (00:00)
        return digits.substring(0, 5);
    };

    // Validate and set from time from input
    const validateAndSetFromTime = (value) => {
        const formatted = formatTimeInput(value);
        setFromInputValue(formatted);

        // Check if it's a valid time when we have 5 characters
        if (formatted.length === 5) {
            const [hours, minutes] = formatted.split(':').map(Number);
            if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
                handleFromTimeChange(formatted);
                return;
            }
        }

        // If not valid, just update the input value
        setFromInputValue(formatted);
    };

    // Validate and set to time from input
    const validateAndSetToTime = (value) => {
        const formatted = formatTimeInput(value);
        setToInputValue(formatted);

        // Check if it's a valid time when we have 5 characters
        if (formatted.length === 5) {
            const [hours, minutes] = formatted.split(':').map(Number);
            if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
                handleToTimeChange(formatted);
                return;
            }
        }

        // If not valid, just update the input value
        setToInputValue(formatted);
    };

    // Sync input values when props change
    useEffect(() => {
        if (fromTime) setFromInputValue(fromTime);
        if (toTime) setToInputValue(toTime);
    }, [fromTime, toTime]);

    useEffect(() => {
        validateTimes();
    }, [fromTime, toTime]);

    const styles = {
        container: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            width: '100%',
        },
        timeInputsContainer: {
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'space-between',
        

        },
        timeInputWrapper: {
            position: 'relative',
            display: 'inline-flex',
        
        },
        timeDisplay: {
            padding: '12px 16px',
            paddingRight: '40px', // Space for icon
            fontSize: '16px',
            border: `1px solid ${error ? '#ef4444' : '#d1d5db'}`,
            borderRadius: '8px',
            outline: 'none',
            backgroundColor: disabled ? '#f9fafb' : '#ffffff',
            cursor: disabled ? 'not-allowed' : 'text',
            width: '100%',
            textAlign: 'left',
        },
        selectTimeLabel: {
            padding: '10px 29px 7px 12px', width: '96px'
            //position: 'absolute',
            //top: '-8px',
            //left: '8px',
            //fontSize: '12px',
            //color: '#6b7280',
            //backgroundColor: '#ffffff',
            //padding: '0 4px',
            //zIndex: 1,
        },
        /* CSS */
        overrideWidth: {
                    width: '100px !important'
        },
        timePicker: {
            position: 'absolute',
            top: '95%',
            left: '78px',
            zIndex: 10,
            backgroundColor: '#ffffff',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxHeight: '200px',
            overflowY: 'auto',
            width: '120px',
            marginTop: '4px',
        },
        timeOption: {
            padding: '8px 16px',
            cursor: 'pointer',
            textAlign: 'center',
            '&:hover': {
                backgroundColor: '#f3f4f6',
            },
        },
        errorMessage: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            fontSize: '14px',
            fontWeight: '500',
        },
        durationDisplay: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px',
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            color: '#1e40af',
        },
        durationText: {
            fontSize: '14px',
            fontWeight: '500',
            display: 'inline-flex',
        },
        durationBadge: {
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: '#dbeafe',
            color: '#1d4ed8',
            padding: '4px 8px',
            borderRadius: '4px',
        },
        clockIconContainer: {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
        },
    };

    return (
        <div style={styles.container} className={className}>
            {/* Time Inputs */}
            <div style={styles.timeInputsContainer}>
                {/* From Time */}
                <div style={styles.timeInputWrapper} ref={fromWrapperRef}>
                    <div style={styles.selectTimeLabel}><strong>{labelFrom}</strong></div>
                    <div >
                        <input
                            type="text"
                            value={fromInputValue}
                            onChange={(e) => validateAndSetFromTime(e.target.value)}
                            disabled={disabled}
                            style={styles.timeDisplay}
                            placeholder="00:00"
                            onFocus={() => setShowFromPicker(true)}
                        />
                        <div style={styles.clockIconContainer}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="9" stroke="#6B7280" strokeWidth="2" />
                                <path d="M12 6V12L16 14" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                    </div>
                    {showFromPicker && (
                        <div style={styles.timePicker}>
                            {timeOptions.map((time) => (
                                <div
                                    key={`from-${time}`}
                                    style={{
                                        ...styles.timeOption,
                                        backgroundColor: time === fromTime ? '#e5e7eb' : 'transparent',
                                    }}
                                    onClick={() => handleFromTimeChange(time)}
                                >
                                    {time}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* To Time */}
                <div style={styles.timeInputWrapper} ref={toWrapperRef}>
                    <div style={{ ...styles.selectTimeLabel  }}><strong>{labelTo}</strong></div>
                    <div >
                        <input
                            type="text"
                            value={toInputValue}
                            onChange={(e) => validateAndSetToTime(e.target.value)}
                            disabled={disabled}
                            style={styles.timeDisplay}
                            placeholder="00:00"
                            onFocus={() => setShowToPicker(true)}
                        />
                        <div style={styles.clockIconContainer}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="9" stroke="#6B7280" strokeWidth="2" />
                                <path d="M12 6V12L16 14" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                    </div>
                    {showToPicker && (
                        <div style={styles.timePicker}>
                            {timeOptions.map((time) => (
                                <div
                                    key={`to-${time}`}
                                    style={{
                                        ...styles.timeOption,
                                        backgroundColor: time === toTime ? '#e5e7eb' : 'transparent',
                                    }}
                                    onClick={() => handleToTimeChange(time)}
                                >
                                    {time}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={styles.errorMessage} role="alert">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#dc2626" />
                        <path d="M15 9L9 15M9 9L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span>{error}</span>
                </div>
            )}

            {/* Duration Display */}
            {fromTime && toTime && !error && (
                <div style={styles.durationDisplay}>
                    <div style={styles.durationText}>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ marginRight: '8px' }}
                        >
                            <circle cx="12" cy="12" r="10" fill="#1e40af" />
                            <path d="M12 6V12L16 14" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        {fromTime} → {toTime}
                    </div>
                    <span style={styles.durationBadge}>{getDuration()}</span>
                </div>
            )}
        </div>
    );
};

export default TimeRangeSelector;